'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ClimatizzatoreItem {
  id: string;
  nome: string;
  modello: string;
  produttore: string;
  stato: string;
  zona_id: string;
  temperatura_impostata: number;
  temperatura_rilevata: number;
  umidita_rilevata: number;
  potenza_kw: number;
  ultima_manutenzione: string;
  prossima_manutenzione: string;
  ip_address: string;
  note: string;
  zona?: { id: string; nome: string };
}

interface AlertAmbientale {
  id: string;
  titolo: string;
  descrizione: string;
  livello: string;
  stato: string;
  created_at: string;
}

const CLIMA_STATO_BG: Record<string, string> = {
  acceso: 'bg-emerald-500/20 text-emerald-400',
  spento: 'bg-gray-500/20 text-gray-400',
  guasto: 'bg-red-500/20 text-red-400',
  allarme: 'bg-red-500/20 text-red-400',
  manutenzione: 'bg-blue-500/20 text-blue-400',
};

const LIVELLO_BG: Record<string, string> = {
  info: 'bg-sky-500/20 text-sky-400',
  warning: 'bg-amber-500/20 text-amber-400',
  critico: 'bg-red-500/20 text-red-400',
  emergenza: 'bg-red-700/30 text-red-300',
};

export default function ClimatizzatoriContent() {
  const [climatizzatori, setClimatizzatori] = useState<ClimatizzatoreItem[]>([]);
  const [alerts, setAlerts] = useState<AlertAmbientale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zone, setZone] = useState<{ id: string; nome: string }[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editItem, setEditItem] = useState<ClimatizzatoreItem | null>(null);
  const [form, setForm] = useState({ nome: '', modello: '', produttore: '', zona_id: '', temperatura_impostata: '20', ip_address: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const openAdd = () => {
    setEditItem(null);
    setForm({ nome: '', modello: '', produttore: '', zona_id: '', temperatura_impostata: '20', ip_address: '', note: '' });
    setShowAddForm(true);
  };

  const openEdit = (c: ClimatizzatoreItem) => {
    setEditItem(c);
    setForm({
      nome: c.nome,
      modello: c.modello || '',
      produttore: c.produttore || '',
      zona_id: c.zona_id || '',
      temperatura_impostata: String(c.temperatura_impostata ?? '20'),
      ip_address: c.ip_address || '',
      note: c.note || '',
    });
    setShowAddForm(true);
  };

  const fetchData = useCallback(async () => {
    try {
      const [climaRes, alertsRes, zoneRes] = await Promise.all([
        supabase.from('climatizzatori').select('*, zona:zone_fisiche(id,nome)').order('nome'),
        supabase.from('alert_ambientali').select('*').in('stato', ['attivo', 'riconosciuto']).order('created_at', { ascending: false }).limit(10),
        supabase.from('zone_fisiche').select('id,nome').order('nome'),
      ]);
      if (climaRes.error) throw climaRes.error;
      if (alertsRes.error) throw alertsRes.error;
      if (zoneRes.error) throw zoneRes.error;
      setClimatizzatori(climaRes.data || []);
      setAlerts(alertsRes.data || []);
      setZone(zoneRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const temp = parseFloat(form.temperatura_impostata);
    if (isNaN(temp) || temp < -10 || temp > 40) { setError('Temperatura impostata deve essere tra -10°C e 40°C'); return; }
    setSaving(true);
    try {
      if (editItem) {
        const { error: err } = await supabase.from('climatizzatori').update({
          nome: form.nome,
          modello: form.modello,
          produttore: form.produttore,
          zona_id: form.zona_id || null,
          temperatura_impostata: temp,
          ip_address: form.ip_address,
          note: form.note,
        }).eq('id', editItem.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('climatizzatori').insert({
          nome: form.nome,
          modello: form.modello,
          produttore: form.produttore,
          zona_id: form.zona_id || null,
          temperatura_impostata: temp,
          ip_address: form.ip_address,
          note: form.note,
          stato: 'acceso',
        });
        if (err) throw err;
      }
      setShowAddForm(false);
      setEditItem(null);
      setForm({ nome: '', modello: '', produttore: '', zona_id: '', temperatura_impostata: '20', ip_address: '', note: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('climatizzatori').delete().eq('id', id);
      if (err) throw err;
      setDeleteConfirmId(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const aggiornaStato = async (id: string, stato: string) => {
    await supabase.from('climatizzatori').update({ stato }).eq('id', id);
    fetchData();
  };

  const riconosciAlert = async (alertId: string) => {
    await supabase.from('alert_ambientali').update({ stato: 'riconosciuto', riconosciuto_at: new Date().toISOString() }).eq('id', alertId);
    fetchData();
  };

  const climaGuasti = climatizzatori.filter(c => c.stato === 'guasto' || c.stato === 'allarme').length;
  const climaAccesi = climatizzatori.filter(c => c.stato === 'acceso').length;
  const alertAttivi = alerts.filter(a => a.stato === 'attivo').length;
  const isManutenzioneSoprascaduta = (data: string) => data && new Date(data) < new Date();

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Climatizzatori Sala CED</h1>
            <p className="text-muted-color text-sm mt-1">Stato climatizzatori, alert guasto e temperatura fuori soglia</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Aggiungi
            </button>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Totali</p>
            <p className="text-2xl font-bold text-white">{climatizzatori.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Accesi</p>
            <p className="text-2xl font-bold text-emerald-400">{climaAccesi}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Guasti / Allarme</p>
            <p className={`text-2xl font-bold ${climaGuasti > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{climaGuasti}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Alert Attivi</p>
            <p className={`text-2xl font-bold ${alertAttivi > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{alertAttivi}</p>
          </div>
        </div>

        {alertAttivi > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ExclamationTriangleIcon" size={18} className="text-amber-400" />
              <span className="text-amber-400 font-semibold text-sm">Alert Attivi ({alertAttivi})</span>
            </div>
            {alerts.filter(a => a.stato === 'attivo').map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-surface rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${LIVELLO_BG[alert.livello] || 'bg-gray-500/20 text-gray-400'}`}>
                    {alert.livello.toUpperCase()}
                  </span>
                  <div>
                    <p className="text-white text-sm font-semibold">{alert.titolo}</p>
                    <p className="text-muted-color text-xs">{alert.descrizione}</p>
                  </div>
                </div>
                <button onClick={() => riconosciAlert(alert.id)} className="text-xs px-3 py-1 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">Riconosci</button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {climatizzatori.map(c => {
            const tempDiff = c.temperatura_rilevata - c.temperatura_impostata;
            const tempOk = Math.abs(tempDiff) <= 2;
            const manutScaduta = isManutenzioneSoprascaduta(c.prossima_manutenzione);
            return (
              <div key={c.id} className={`bg-surface rounded-xl border p-5 ${c.stato === 'guasto' || c.stato === 'allarme' ? 'border-red-500/50' : manutScaduta ? 'border-amber-500/40' : 'border-border'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-white font-semibold">{c.nome}</p>
                    <p className="text-muted-color text-xs">{c.produttore} {c.modello}</p>
                    <p className="text-muted-color text-xs">{c.zona?.nome || '—'}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CLIMA_STATO_BG[c.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                    {c.stato.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-surface-elevated rounded-lg p-2 text-center">
                    <p className="text-muted-color text-xs mb-0.5">Impostata</p>
                    <p className="text-sm font-bold text-white">{c.temperatura_impostata}°C</p>
                  </div>
                  <div className={`rounded-lg p-2 text-center ${tempOk ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                    <p className="text-muted-color text-xs mb-0.5">Rilevata</p>
                    <p className={`text-sm font-bold ${tempOk ? 'text-emerald-400' : 'text-red-400'}`}>{c.temperatura_rilevata}°C</p>
                  </div>
                  <div className="bg-surface-elevated rounded-lg p-2 text-center">
                    <p className="text-muted-color text-xs mb-0.5">Umidità</p>
                    <p className="text-sm font-bold text-sky-400">{c.umidita_rilevata}%</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-color mb-3">
                  <span>Potenza: {c.potenza_kw} kW</span>
                  {c.ip_address && <span className="font-mono">{c.ip_address}</span>}
                </div>
                {manutScaduta && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-3">
                    <p className="text-amber-400 text-xs font-semibold">⚠ Manutenzione scaduta il {new Date(c.prossima_manutenzione).toLocaleDateString('it-IT')}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  {c.stato !== 'acceso' && (
                    <button onClick={() => aggiornaStato(c.id, 'acceso')} className="flex-1 text-xs px-2 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors">Accendi</button>
                  )}
                  {c.stato !== 'spento' && (
                    <button onClick={() => aggiornaStato(c.id, 'spento')} className="flex-1 text-xs px-2 py-1.5 bg-gray-500/10 text-gray-400 rounded-lg hover:bg-gray-500/20 transition-colors">Spegni</button>
                  )}
                  {c.stato !== 'manutenzione' && (
                    <button onClick={() => aggiornaStato(c.id, 'manutenzione')} className="flex-1 text-xs px-2 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors">Manutenzione</button>
                  )}
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                    <Icon name="PencilIcon" size={14} />
                  </button>
                  <button onClick={() => setDeleteConfirmId(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                    <Icon name="TrashIcon" size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {climatizzatori.length === 0 && (
            <div className="col-span-3 text-center py-12 text-muted-color">Nessun climatizzatore trovato</div>
          )}
        </div>

        {/* Delete Confirm */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-semibold text-white mb-2">Elimina Climatizzatore</h3>
              <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare questo climatizzatore?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold">Elimina</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Aggiungi/Modifica */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{editItem ? 'Modifica Climatizzatore' : 'Aggiungi Climatizzatore'}</h3>
                <button onClick={() => { setShowAddForm(false); setEditItem(null); }} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                  <input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} required
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Produttore</label>
                    <input value={form.produttore} onChange={e => setForm(p => ({ ...p, produttore: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Modello</label>
                    <input value={form.modello} onChange={e => setForm(p => ({ ...p, modello: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={form.zona_id} onChange={e => setForm(p => ({ ...p, zona_id: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Temp. Impostata (°C) -10 a 40</label>
                    <input type="number" step="0.5" min="-10" max="40" value={form.temperatura_impostata} onChange={e => setForm(p => ({ ...p, temperatura_impostata: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">IP Address</label>
                  <input value={form.ip_address} onChange={e => setForm(p => ({ ...p, ip_address: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowAddForm(false); setEditItem(null); }} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Salva'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
